/**
 * Yallet RWA SDK - Minting Module
 *
 * Handles cNFT minting using Metaplex Bubblegum
 */

import {
  createUmi,
  publicKey,
  keypairIdentity,
  type Umi,
  type PublicKey,
  type Keypair,
} from '@metaplex-foundation/umi';
import { createUmi as createUmiDefaults } from '@metaplex-foundation/umi-bundle-defaults';
import {
  mplBubblegum,
  mintV1,
  findLeafAssetIdPda,
  type MetadataArgsArgs,
} from '@metaplex-foundation/mpl-bubblegum';
import { Connection, PublicKey as SolanaPublicKey } from '@solana/web3.js';

import type {
  TreeConfig,
  MintResult,
  Signer,
  SDKConfig,
} from './types.js';
import { AssetType } from './types.js';

// ======================================
//  Minter Class
// ======================================

export class CNFTMinter {
  private umi: Umi;
  private connection: Connection;
  private config: SDKConfig;
  private treeConfig: TreeConfig | null = null;

  constructor(config: SDKConfig) {
    this.config = config;
    this.connection = new Connection(config.rpcEndpoint);

    // Initialize Umi
    this.umi = createUmiDefaults(config.rpcEndpoint).use(mplBubblegum());
  }

  /**
   * Set the signer for minting transactions
   *
   * @param signer - Signer with publicKey and signTransaction
   */
  setSigner(keypair: Keypair): void {
    this.umi = this.umi.use(keypairIdentity(keypair));
  }

  /**
   * Set the Merkle tree configuration
   *
   * @param treeConfig - Tree configuration
   */
  setTreeConfig(treeConfig: TreeConfig): void {
    this.treeConfig = treeConfig;
  }

  /**
   * Get or fetch tree configuration
   */
  async getTreeConfig(): Promise<TreeConfig | null> {
    if (this.treeConfig) {
      return this.treeConfig;
    }

    // Try to fetch from configured address
    if (this.config.merkleTreeAddress) {
      // For now, return a basic config
      // In production, you'd fetch the actual tree data
      return {
        treeAddress: this.config.merkleTreeAddress,
        treeAuthority: this.config.merkleTreeAuthority || this.config.merkleTreeAddress,
        maxDepth: 14,
        capacity: 16384,
        used: 0,
        remaining: 16384,
        usagePercent: 0,
      };
    }

    // Try to fetch from backend API
    if (this.config.backendApiUrl) {
      try {
        const response = await fetch(
          `${this.config.backendApiUrl}/api/cnft/active-tree?network=${this.config.network}`
        );

        if (response.ok) {
          const data = (await response.json()) as {
            available?: boolean;
            tree?: {
              address: string;
              authority: string;
              maxDepth: number;
              capacity: number;
              used: number;
              remaining: number;
              usagePercent: string;
            };
          };
          if (data.available && data.tree) {
            this.treeConfig = {
              treeAddress: data.tree.address,
              treeAuthority: data.tree.authority,
              maxDepth: data.tree.maxDepth,
              capacity: data.tree.capacity,
              used: data.tree.used,
              remaining: data.tree.remaining,
              usagePercent: parseFloat(data.tree.usagePercent),
            };
            return this.treeConfig;
          }
        }
      } catch (err) {
        console.warn('Failed to fetch tree from backend:', err);
      }
    }

    return null;
  }

  /**
   * Mint a compressed NFT
   *
   * @param params - Minting parameters
   * @returns Mint result
   */
  async mint(params: {
    metadataUri: string;
    name: string;
    symbol?: string;
    recipient: string;
    sellerFeeBasisPoints?: number;
    creators?: Array<{ address: string; share: number }>;
  }): Promise<MintResult> {
    const {
      metadataUri,
      name,
      symbol = 'YRWA',
      recipient,
      sellerFeeBasisPoints = 0,
      creators = [],
    } = params;

    // Get tree configuration
    const treeConfig = await this.getTreeConfig();

    if (!treeConfig) {
      return {
        success: false,
        error: 'No Merkle tree configured. Please set a tree address or backend API.',
      };
    }

    try {
      const merkleTree = publicKey(treeConfig.treeAddress);
      const leafOwner = publicKey(recipient);

      // Prepare metadata args
      const metadataArgs: MetadataArgsArgs = {
        name,
        symbol,
        uri: metadataUri,
        sellerFeeBasisPoints,
        primarySaleHappened: false,
        isMutable: true,
        editionNonce: null,
        tokenStandard: null,
        collection: null,
        uses: null,
        tokenProgramVersion: 0, // Original
        creators: creators.map((c) => ({
          address: publicKey(c.address),
          verified: false,
          share: c.share,
        })),
      };

      // Build and send mint instruction
      const { signature } = await mintV1(this.umi, {
        merkleTree,
        leafOwner,
        metadata: metadataArgs,
      }).sendAndConfirm(this.umi);

      // Get the leaf index to compute asset ID
      // Note: In production, you'd parse the transaction logs
      const signatureStr = Buffer.from(signature).toString('base64');

      // Notify backend of successful mint (if configured)
      if (this.config.backendApiUrl) {
        try {
          await fetch(`${this.config.backendApiUrl}/api/cnft/mint-complete`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              network: this.config.network,
              treeAddress: treeConfig.treeAddress,
            }),
          });
        } catch (err) {
          console.warn('Failed to notify backend of mint:', err);
        }
      }

      return {
        success: true,
        signature: signatureStr,
        // Asset ID would be computed from the leaf index
        // For now, we return the signature
      };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      return {
        success: false,
        error: `Minting failed: ${errorMessage}`,
      };
    }
  }
}

// ======================================
//  Helper Functions
// ======================================

/**
 * Create a new CNFTMinter instance
 *
 * @param config - SDK configuration
 * @returns CNFTMinter instance
 */
export function createMinter(config: SDKConfig): CNFTMinter {
  return new CNFTMinter(config);
}

/**
 * Estimate minting cost
 *
 * @param connection - Solana connection
 * @returns Estimated cost in lamports
 */
export async function estimateMintCost(connection: Connection): Promise<number> {
  // cNFT minting is very cheap - typically around 0.00001 SOL
  // The main cost is the priority fee
  const recentPrioritizationFees = await connection.getRecentPrioritizationFees();
  const avgFee = recentPrioritizationFees.reduce(
    (sum, fee) => sum + fee.prioritizationFee,
    0
  ) / (recentPrioritizationFees.length || 1);

  // Base transaction fee + priority fee
  const baseFee = 5000; // 0.000005 SOL
  const priorityFee = Math.ceil(avgFee);

  return baseFee + priorityFee;
}
